import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, CalendarClock, X, Video, MapPin, ArrowLeftRight } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { STATUS } from '../lib/appointments';
import { t, formatDate, formatTime } from '../i18n';
import { Button, Badge, Alert, Spinner } from './ui';
import RescheduleModal from './RescheduleModal';
import ModalityModal from './ModalityModal';
import CancelSessionModal from './CancelSessionModal';

/**
 * A patient's next sessions, opened from their row.
 *
 * Cancelling used to live only in Agenda del día, which shows one day at a
 * time — so calling off a session three weeks out meant clicking forward
 * through twenty empty days to find it. The natural question is "what does this
 * person have coming up", and the answer is the right place to act on it: move
 * a session, cancel it, or switch it — or several — between in person and
 * online.
 *
 * Loaded when the row is opened rather than with the list: most rows are never
 * expanded, and a query per patient on every page load would be paid for
 * nothing.
 */

const PatientUpcoming = ({ patient, onChange }) => {
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [cancelling, setCancelling] = useState(null);
  const [moving, setMoving] = useState(null);
  const [modality, setModality] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: loadError } = await supabase
      .from('appointments')
      .select('id, therapist_id, starts_at, ends_at, status, billing_mode, patient_amount, patient_payment_status, location_id, locations(name, is_online)')
      .eq('dependent_id', patient.id)
      .in('status', ['scheduled', 'waiting', 'in_session'])
      .gte('starts_at', new Date().toISOString())
      .order('starts_at');
    setLoading(false);
    if (loadError) { setError(loadError.message); return; }
    setAppointments(data ?? []);
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  const patientName = `${patient.first_name} ${patient.last_name}`;

  return (
    <>
      <div>
        {error && <Alert tone="danger">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
        {loading ? <Spinner /> : appointments.length === 0 ? (
          <p className="text-xs text-slate-500">{t('patients.upcomingNone')}</p>
        ) : (
          <>
            {appointments.length > 1 && (
              <div className="mb-2 flex justify-end">
                {/* A button, like the other ways into a flow: small blue text
                    went unnoticed before (12 Sep 2026). */}
                <Button
                  variant="secondary"
                  onClick={() => setModality({ kind: 'next', count: appointments.length })}
                >
                  <ArrowLeftRight size={14} />{t('modality.changeMany')}
                </Button>
              </div>
            )}
            <ul className="space-y-2">
              {appointments.map((appointment) => (
                <li key={appointment.id} className="flex items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <CalendarDays size={13} className="shrink-0 text-slate-400" />
                    <span className="font-medium text-slate-800 first-letter:uppercase">
                      {formatDate(appointment.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                    <span className="text-slate-600">
                      {formatTime(appointment.starts_at)}–{formatTime(appointment.ends_at)}
                    </span>
                    {appointment.locations?.name && (
                      <Badge tone={appointment.locations.is_online ? 'info' : 'neutral'}>
                        {appointment.locations.is_online ? <Video size={11} /> : <MapPin size={11} />}
                        {appointment.locations.name}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {appointment.status === STATUS.SCHEDULED && (
                      <Button
                        variant="ghost"
                        onClick={() => setModality({ kind: 'one', id: appointment.id })}
                        title={t('modality.change')}
                      >
                        <ArrowLeftRight size={14} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      onClick={() => setMoving(appointment)}
                      title={t('appointments.reschedule')}
                    >
                      <CalendarClock size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setCancelling(appointment)}
                      title={t('appointments.cancelAppointment')}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Who asked to cancel, when they gave notice, and whether it is charged,
          as in Agenda del día (0052). */}
      {cancelling && (
        <CancelSessionModal
          appointment={cancelling}
          patientName={patientName}
          onClose={() => setCancelling(null)}
          onDone={() => { setCancelling(null); load(); onChange?.(); }}
        />
      )}

      {moving && (
        <RescheduleModal
          appointment={moving}
          therapistId={moving.therapist_id}
          patientName={patientName}
          onClose={() => setMoving(null)}
          onDone={() => { setNotice(t('reschedule.done')); load(); onChange?.(); }}
        />
      )}

      {modality && (
        <ModalityModal
          patientName={patientName}
          sessions={appointments}
          initialScope={modality}
          onClose={() => setModality(null)}
          onDone={(message) => { if (message) setNotice(message); load(); onChange?.(); }}
        />
      )}
    </>
  );
};

export default PatientUpcoming;
